import {
  createContext,
  useContext,
  useState,
  ReactNode,
} from "react";

export interface SiteSearchItem {   
  id: number;
  title: string;
  content: string;
  path: string;
  section?: string | null;
  keywords?: string | null;
  source_type?: "manual" | "product" | "category" | "service" | "page" | "career";
  source_id?: number | null;
  status?: "active" | "inactive";
  created_at?: string;
  updated_at?: string;
}

interface SiteSearchContextType {
  query: string;
  results: SiteSearchItem[];
  loading: boolean;
  performSearch: (text: string) => Promise<void>;
  clearSearch: () => void;
}

const SiteSearchContext = createContext<SiteSearchContextType | null>(null);

export const useSiteSearch = () => {
  const context = useContext(SiteSearchContext);

  if (!context) {
    throw new Error("useSiteSearch must be used within SiteSearchProvider");
  }

  return context;
};

interface SiteSearchProviderProps {
  children: ReactNode;
}

export const SiteSearchProvider = ({
  children,
}: SiteSearchProviderProps) => {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const [query, setQuery] = useState<string>("");
  const [results, setResults] = useState<SiteSearchItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const performSearch = async (text: string) => {
    const trimmedText = text.trim();

    setQuery(trimmedText);

    if (!trimmedText) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(
        `${BASE_URL}/search?q=${encodeURIComponent(trimmedText)}`,
        {
          method: "GET",
        },
      );

      const result = await res.json();
      if (result.success) {
        setResults(result.data || []);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error("Site search error:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setLoading(false);
  };

  return (
    <SiteSearchContext.Provider
      value={{
        query,
        results,
        loading,
        performSearch,
        clearSearch,
      }}
    >
      {children}
    </SiteSearchContext.Provider>
  );
};